import React, { useState, useEffect } from 'react';
import { Bot, Plus, Edit, Trash2, Settings, TestTube } from 'lucide-react';
import { whatsappAPI } from '../../services/api';
import { toast } from 'react-hot-toast';

const WhatsAppAiAssistants = () => {
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAssistant, setEditingAssistant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    model: 'gpt-3.5-turbo',
    systemPrompt: '',
    maxTokens: 500,
    temperature: 0.7,
    contextWindow: 4000,
    isActive: true
  });

  useEffect(() => {
    loadAssistants();
  }, []);

  const loadAssistants = async () => {
    try {
      setLoading(true);
      const response = await whatsappAPI.getAiAssistants();
      if (response.data.success) {
        setAssistants(response.data.data);
      }
    } catch (error) {
      console.error('Error loading AI assistants:', error);
      toast.error('Failed to load AI assistants');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      model: 'gpt-3.5-turbo',
      systemPrompt: '',
      maxTokens: 500,
      temperature: 0.7,
      contextWindow: 4000,
      isActive: true
    });
    setEditingAssistant(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.systemPrompt) {
      toast.error('Name and system prompt are required');
      return;
    }

    try {
      if (editingAssistant) {
        await whatsappAPI.updateAiAssistant(editingAssistant.id, formData);
        toast.success('AI assistant updated successfully');
      } else {
        await whatsappAPI.createAiAssistant(formData);
        toast.success('AI assistant created successfully');
      }
      
      setShowForm(false);
      resetForm();
      loadAssistants();
    } catch (error) {
      console.error('Error saving AI assistant:', error);
      toast.error('Failed to save AI assistant');
    }
  };

  const handleEdit = (assistant) => {
    setEditingAssistant(assistant);
    setFormData({
      name: assistant.name,
      description: assistant.description || '',
      model: assistant.model,
      systemPrompt: assistant.system_prompt,
      maxTokens: assistant.max_tokens,
      temperature: assistant.temperature,
      contextWindow: assistant.context_window,
      isActive: assistant.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this AI assistant?')) {
      return;
    }

    try {
      await whatsappAPI.deleteAiAssistant(id);
      toast.success('AI assistant deleted successfully');
      loadAssistants();
    } catch (error) {
      console.error('Error deleting AI assistant:', error);
      toast.error('Failed to delete AI assistant');
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <Bot className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
            AI Assistants
          </h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            Manage AI assistants for WhatsApp auto-replies
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Assistant
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {editingAssistant ? 'Edit AI Assistant' : 'Create New AI Assistant'}
            </h3>
            <button
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter assistant name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Model
                </label>
                <select
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="auto">🔄 Auto (Smart Model Selection)</option>
                  
                  <optgroup label="Latest GPT-5 Models (2024-2025)">
                    <option value="gpt-5">GPT-5 (Latest & Most Advanced - 2024)</option>
                    <option value="gpt-5-turbo">GPT-5 Turbo (High Performance - 2024)</option>
                    <option value="gpt-5o">GPT-5o (Latest Omni Model - 2024)</option>
                    <option value="gpt-5o-mini">GPT-5o Mini (Fast & Cost-effective - 2024)</option>
                  </optgroup>
                  
                  <optgroup label="Latest GPT-4 Models (2024-2025)">
                    <option value="gpt-4o">GPT-4o (Latest & Most Capable - 2024)</option>
                    <option value="gpt-4o-mini">GPT-4o Mini (Fast & Cost-effective - 2024)</option>
                    <option value="gpt-4-turbo-preview">GPT-4 Turbo Preview (Latest Features - 2024)</option>
                    <option value="gpt-4-turbo">GPT-4 Turbo (High Performance)</option>
                    <option value="gpt-4">GPT-4 (Standard)</option>
                    <option value="gpt-4-32k">GPT-4 32K (Long Context)</option>
                    <option value="gpt-4-1106-preview">GPT-4 Turbo 1106 Preview (Latest)</option>
                    <option value="gpt-4-0125-preview">GPT-4 Turbo 0125 Preview (Latest)</option>
                    <option value="gpt-4-0613">GPT-4 0613 (Stable)</option>
                    <option value="gpt-4o-2024-05-13">GPT-4o 2024-05-13 (Latest Vision)</option>
                    <option value="gpt-4o-mini-2024-07-18">GPT-4o Mini 2024-07-18 (Latest)</option>
                    <option value="gpt-4-all">GPT-4 All (Multimodal)</option>
                    <option value="gpt-4o-2024-07-18">GPT-4o 2024-07-18 (Latest Stable)</option>
                    <option value="gpt-4o-mini-2024-07-18">GPT-4o Mini 2024-07-18 (Latest Stable)</option>
                  </optgroup>
                  
                  <optgroup label="Latest GPT-3.5 Models (2024-2025)">
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Fast & Reliable)</option>
                    <option value="gpt-3.5-turbo-16k">GPT-3.5 Turbo 16K (Extended Context)</option>
                    <option value="gpt-3.5-turbo-instruct">GPT-3.5 Turbo Instruct (Instruction Following)</option>
                    <option value="gpt-3.5-turbo-1106">GPT-3.5 Turbo 1106 (Latest Version)</option>
                    <option value="gpt-3.5-turbo-0125">GPT-3.5 Turbo 0125 (Latest Version)</option>
                    <option value="gpt-3.5-turbo-0613">GPT-3.5 Turbo 0613 (Stable)</option>
                  </optgroup>
                  
                  <optgroup label="Specialized OpenAI Models">
                    <option value="gpt-4-vision-preview">GPT-4 Vision (Image + Text)</option>
                  </optgroup>
                  
                  <optgroup label="Embedding Models">
                    <option value="text-embedding-3-large">Text Embedding 3 Large (Latest)</option>
                    <option value="text-embedding-3-small">Text Embedding 3 Small (Fast)</option>
                    <option value="text-embedding-ada-002">Text Embedding Ada 002 (Stable)</option>
                  </optgroup>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Choose the AI model for this assistant. "Auto" will intelligently select the best model based on the task requirements.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter assistant description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                System Prompt *
              </label>
              <textarea
                value={formData.systemPrompt}
                onChange={(e) => setFormData({ ...formData, systemPrompt: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter system prompt for the AI assistant"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Tokens
                </label>
                <input
                  type="number"
                  value={formData.maxTokens}
                  onChange={(e) => setFormData({ ...formData, maxTokens: parseInt(e.target.value) })}
                  min="1"
                  max="4000"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Temperature
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.temperature}
                  onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
                  min="0"
                  max="2"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Context Window
                </label>
                <input
                  type="number"
                  value={formData.contextWindow}
                  onChange={(e) => setFormData({ ...formData, contextWindow: parseInt(e.target.value) })}
                  min="1000"
                  max="16000"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Active
              </label>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                {editingAssistant ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Assistants List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">AI Assistants</h3>
        </div>

        {loading ? (
          <div className="p-4 sm:p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Loading assistants...</p>
          </div>
        ) : assistants.length === 0 ? (
          <div className="p-4 sm:p-6 text-center">
            <Bot className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No AI assistants found</p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Create your first assistant
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {assistants.map((assistant) => (
              <div key={assistant.id} className="p-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                        {assistant.name}
                      </h4>
                      {assistant.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                          Inactive
                        </span>
                      )}
                    </div>
                    
                    {assistant.description && (
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {assistant.description}
                      </p>
                    )}
                    
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Model:</span>
                        <span className="ml-1 text-gray-900 dark:text-white">{assistant.model}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Max Tokens:</span>
                        <span className="ml-1 text-gray-900 dark:text-white">{assistant.max_tokens}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Temperature:</span>
                        <span className="ml-1 text-gray-900 dark:text-white">{assistant.temperature}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Created:</span>
                        <span className="ml-1 text-gray-900 dark:text-white">
                          {new Date(assistant.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(assistant)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(assistant.id)}
                      className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppAiAssistants;
